import React from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Typography,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format, isPast, isToday } from 'date-fns';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import type { TaskWithClient } from '../../types/task';

interface TaskItemProps {
  task: TaskWithClient;
  onToggleComplete: (task: TaskWithClient) => void;
  onEdit: (task: TaskWithClient) => void;
  onDelete: (task: TaskWithClient) => void;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onToggleComplete,
  onEdit,
  onDelete,
}) => {
  const isCompleted = task.status === 'completed';
  const isOverdue = task.dueDate && isPast(new Date(task.dueDate)) && !isCompleted;
  const isDueToday = task.dueDate && isToday(new Date(task.dueDate));

  const clientName = task.clientId
    ? `${task.clientId.firstName} ${task.clientId.lastName}`
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: isOverdue ? 'error.light' : 'divider',
        backgroundColor: isCompleted ? 'action.hover' : 'background.paper',
        opacity: isCompleted ? 0.7 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'action.hover',
          borderColor: 'primary.light',
        },
      }}
    >
      {/* Checkbox */}
      <Checkbox
        checked={isCompleted}
        onChange={() => onToggleComplete(task)}
        sx={{ mt: -0.5 }}
      />

      {/* Main Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            textDecoration: isCompleted ? 'line-through' : 'none',
            color: isCompleted ? 'text.secondary' : 'text.primary',
            mb: 0.5,
          }}
        >
          {task.title}
        </Typography>

        {/* Description */}
        {task.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {task.description}
          </Typography>
        )}

        {/* Meta info */}
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {/* Priority */}
          <TaskPriorityBadge priority={task.priority} size="small" />

          {/* Due date */}
          {task.dueDate && (
            <Chip
              icon={<EventIcon />}
              label={
                isDueToday
                  ? `Today${task.dueTime ? ` at ${task.dueTime}` : ''}`
                  : format(new Date(task.dueDate), 'MMM d')
              }
              size="small"
              color={isOverdue ? 'error' : isDueToday ? 'warning' : 'default'}
              variant="outlined"
            />
          )}

          {/* Client */}
          {clientName && (
            <Chip
              icon={<PersonIcon />}
              label={clientName}
              size="small"
              variant="outlined"
            />
          )}

          {/* Tags */}
          {task.tags?.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ borderStyle: 'dashed' }}
            />
          ))}
        </Stack>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(task)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(task)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default TaskItem;
